import { AMBIENTE_PADRAO, FRACOES_RADIANTES, bolaDeFogo, massaNoTanqueKg, type BolaDeFogo } from '../bleve'
import { perfilParaEstado, volumeTanqueM3 } from '../caminhaoTanque/perfil'
import { classeEstabilidade, contornoNoSolo, type ClasseEstabilidade } from '../dispersao'
import { acaoProtetoraGre, comportaComoGasDenso, limitesDoProduto, motivoSemPluma, type AcaoProtetoraGre, type LimiteDesenhavel } from '../dispersao/produto'
import { PRODUTOS, type Produto } from '../produtos'
import { zonasPorRadiacao, type ZonasRadiacao } from '../zonas'
import { meteorologiaDaCena } from './operacoes'
import type { CenaTabletop, MeteorologiaTabletop, ObjetoTabletop, TipoObjetoTabletop } from './tipos'

/**
 * Camadas calculadas da mesa: para cada objeto com fonte de produto, os anéis
 * de BLEVE (tanque em fogo) ou os contornos da pluma (vazamento), com as
 * distâncias do GRE. Recalculadas a cada mudança de vento, produto ou
 * situação — é o que faz a injeção "o vento mudou" mexer na mesa.
 *
 * Reaproveita os mesmos módulos das oficinas: o número da mesa é o número
 * que o aluno viu na oficina.
 */

/** Vazão usada quando o instrutor marca vazamento sem informar a taxa, kg/s. */
export const VAZAO_PADRAO_KG_S = 2

export interface ContornoPlumaMesa {
  limite: LimiteDesenhavel
  borda: Array<[number, number]>
  alcanceM: number
}

export interface CamadasFonte {
  objeto: ObjetoTabletop
  produto: Produto
  /** Posição da fonte no chão (x, z). */
  origem: readonly [number, number]
  bleve: { bola: BolaDeFogo; zonas: ZonasRadiacao } | null
  pluma: { classe: ClasseEstabilidade; vazaoKgS: number; contornos: ContornoPlumaMesa[] } | null
  /** Isolamento do GRE para a situação atual, m. */
  isolamentoGreM: number
  acaoProtetora: AcaoProtetoraGre | null
  avisos: string[]
}

export function classeDaMeteorologia(m: MeteorologiaTabletop): ClasseEstabilidade {
  return m.periodo === 'dia'
    ? classeEstabilidade({ ventoMS: m.ventoMS, periodo: 'dia', insolacao: m.ceuEncoberto ? 'encoberto' : 'moderada' })
    : classeEstabilidade({ ventoMS: m.ventoMS, periodo: 'noite', nebulosidade: m.ceuEncoberto ? 'nublado' : 'poucaNuvem' })
}

export function camadasDaFonte(objeto: ObjetoTabletop, meteorologia: MeteorologiaTabletop): CamadasFonte | null {
  const fonte = objeto.fonte
  if (!fonte) return null
  const produto = PRODUTOS.find((p) => p.id === fonte.idProduto)
  if (!produto) return null
  const origem = [objeto.transformacao.posicao[0], objeto.transformacao.posicao[2]] as const
  const situacao = fonte.situacao ?? 'estavel'
  const avisos: string[] = []

  let bleve: CamadasFonte['bleve'] = null
  if (situacao === 'tanqueEmFogo') {
    const podeBolaDeFogo = produto.estadoTransporte === 'gasLiquefeitoPressurizado' && produto.calorCombustaoMJKg && produto.densidadeLiquidoKgM3
    if (podeBolaDeFogo) {
      const massaKg = massaNoTanqueKg(volumeTanqueM3(perfilParaEstado(produto.estadoTransporte).tanque), fonte.enchimento, produto.densidadeLiquidoKgM3!.valor)
      if (massaKg > 0) {
        const bola = bolaDeFogo({ massaKg, calorCombustaoMJKg: produto.calorCombustaoMJKg!.valor, fracaoRadiante: FRACOES_RADIANTES.acimaDoAlivio })
        bleve = { bola, zonas: zonasPorRadiacao(bola, AMBIENTE_PADRAO, produto.gre.isolamentoTanqueEmFogoM.valor) }
      }
    } else {
      avisos.push(`${produto.nome}: sem bola de fogo de BLEVE neste laboratório; vale o isolamento do GRE.`)
    }
  }

  let pluma: CamadasFonte['pluma'] = null
  let acaoProtetora: AcaoProtetoraGre | null = null
  if (situacao === 'vazamento') {
    const motivo = motivoSemPluma(produto)
    if (motivo) {
      avisos.push(`${produto.nome}: ${motivo}`)
    } else {
      const classe = classeDaMeteorologia(meteorologia)
      const vazaoKgS = fonte.vazaoKgS ?? VAZAO_PADRAO_KG_S
      const parametros = { vazaoKgS, ventoMS: meteorologia.ventoMS, classe, terreno: meteorologia.terreno, alturaFonteM: 0 }
      const contornos = limitesDoProduto(produto)
        .map((limite) => {
          const borda = contornoNoSolo(parametros, limite.mgM3 * 1e-6, 40)
          return { limite, borda, alcanceM: borda.length ? borda[borda.length - 1]![0] : 0 }
        })
        .filter((c) => c.borda.length > 0)
      pluma = { classe, vazaoKgS, contornos }
      if (comportaComoGasDenso(produto)) avisos.push(`${produto.nome}: gás denso — a pluma calculada subestima o espalhamento junto ao solo.`)
    }
    acaoProtetora = acaoProtetoraGre(produto, { noite: meteorologia.periodo === 'noite', ventoMS: meteorologia.ventoMS, grandeVazamento: true })
  }

  const isolamentoGreM =
    situacao === 'tanqueEmFogo' ? produto.gre.isolamentoTanqueEmFogoM.valor : acaoProtetora?.isolarM ?? produto.gre.isolamentoInicialM.valor

  return { objeto, produto, origem, bleve, pluma, isolamentoGreM, acaoProtetora, avisos }
}

export function camadasDaCena(cena: CenaTabletop): CamadasFonte[] {
  const meteorologia = meteorologiaDaCena(cena)
  return cena.objetos
    .filter((objeto) => !objeto.oculto)
    .map((objeto) => camadasDaFonte(objeto, meteorologia))
    .filter((camada): camada is CamadasFonte => camada !== null)
}

/** Bombeiros e viaturas — não a fonte em si, nem vítimas ou cenário. */
const TIPOS_EM_RISCO: ReadonlySet<TipoObjetoTabletop> = new Set([
  'bombeiroNivelA',
  'bombeiroNivelB',
  'bombeiroNivelC',
  'bombeiroIncendio',
  'medico',
  'viaturaHazmat',
  'viaturaAbt',
  'ambulancia',
  'carro',
])

export interface ObjetoEmRisco {
  idObjeto: string
  idFonte: string
  distanciaM: number
}

/**
 * Bombeiros/viaturas posicionados dentro do isolamento do GRE de alguma
 * fonte — o mesmo raio que a mesa já desenha como anel tracejado, calculado
 * igual para tanque em fogo ou vazamento (`isolamentoGreM`). Mecânica leve:
 * só sinaliza, não bloqueia nada.
 */
export function objetosEmRisco(cena: CenaTabletop, camadas: readonly CamadasFonte[]): ObjetoEmRisco[] {
  const resultado: ObjetoEmRisco[] = []
  for (const objeto of cena.objetos) {
    if (objeto.oculto || !TIPOS_EM_RISCO.has(objeto.tipo)) continue
    const [x, , z] = objeto.transformacao.posicao
    for (const camada of camadas) {
      if (camada.objeto.id === objeto.id) continue
      const distanciaM = Math.hypot(x - camada.origem[0], z - camada.origem[1])
      if (distanciaM <= camada.isolamentoGreM) {
        resultado.push({ idObjeto: objeto.id, idFonte: camada.objeto.id, distanciaM })
      }
    }
  }
  return resultado
}
